import { Controller } from '@hotwired/stimulus';

const HIDE_DELAY_MS = 120;
const TEASER_SELECTOR = '[data-chip-tooltip-teaser]';

// One flyout per transcript panel, sibling of the scroll region so overflow does not clip it.
export default class extends Controller {
  static targets = ['scroll', 'tip', 'message'];

  connect() {
    this.anchor = null;
    this.hideTimer = null;
    this.tipHeight = null;
    this.boundLinks = new WeakSet();
    this.linkAbort = new AbortController();

    this.onScroll = () => {
      if (this.anchor) this.position();
    };
    this.onResize = () => {
      if (!this.anchor) return;
      this.tipHeight = null;
      this.position();
    };

    if (this.hasScrollTarget) {
      this.bindLinks();
      this.mutationObserver = new MutationObserver(() => this.bindLinks());
      this.mutationObserver.observe(this.scrollTarget, { childList: true, subtree: true });
      this.scrollTarget.addEventListener('scroll', this.onScroll, { passive: true });
    }
    window.addEventListener('resize', this.onResize, { passive: true });
  }

  disconnect() {
    this.linkAbort.abort();
    this.mutationObserver?.disconnect();
    if (this.hasScrollTarget) {
      this.scrollTarget.removeEventListener('scroll', this.onScroll);
    }
    window.removeEventListener('resize', this.onResize);
    this.clearHideTimer();
    this.deactivate();
  }

  bindLinks() {
    if (!this.hasScrollTarget) return;

    const { signal } = this.linkAbort;
    this.scrollTarget.querySelectorAll(TEASER_SELECTOR).forEach((link) => {
      if (this.boundLinks.has(link)) return;
      this.boundLinks.add(link);

      link.addEventListener(
        'mouseenter',
        () => {
          this.clearHideTimer();
          this.activate(link);
        },
        { signal }
      );

      link.addEventListener(
        'mouseleave',
        (event) => {
          const related = event.relatedTarget;
          if (related && link.contains(related)) return;
          if (related instanceof Element && related.closest(TEASER_SELECTOR)) return;
          this.scheduleHide();
        },
        { signal }
      );

      link.addEventListener(
        'focus',
        () => {
          this.clearHideTimer();
          this.activate(link);
        },
        { signal }
      );

      link.addEventListener('blur', () => this.scheduleHide(), { signal });
    });
  }

  activate(anchor) {
    const teaser = anchor.dataset.chipTooltipTeaser;
    if (!teaser || !this.hasTipTarget || !this.hasMessageTarget) return;

    if (this.anchor && this.anchor !== anchor) {
      this.anchor.removeAttribute('aria-describedby');
    }

    this.anchor = anchor;
    if (this.messageTarget.textContent !== teaser) {
      this.messageTarget.textContent = teaser;
      this.tipHeight = null;
    }

    if (!this.tipTarget.id) {
      this.tipTarget.id = `chip-tooltip-${Math.random().toString(36).slice(2, 9)}`;
    }
    anchor.setAttribute('aria-describedby', this.tipTarget.id);

    this.tipTarget.classList.add('chip-tooltip-flyout--visible');
    this.tipTarget.style.display = 'block';
    this.tipTarget.setAttribute('aria-hidden', 'false');
    this.position();
  }

  scheduleHide() {
    this.clearHideTimer();
    this.hideTimer = window.setTimeout(() => this.deactivate(), HIDE_DELAY_MS);
  }

  clearHideTimer() {
    if (!this.hideTimer) return;
    clearTimeout(this.hideTimer);
    this.hideTimer = null;
  }

  deactivate() {
    if (this.anchor) {
      this.anchor.removeAttribute('aria-describedby');
      this.anchor = null;
    }
    if (!this.hasTipTarget) return;

    this.tipTarget.classList.remove('chip-tooltip-flyout--visible');
    this.tipTarget.style.display = '';
    this.tipTarget.setAttribute('aria-hidden', 'true');
    this.tipTarget.style.left = '';
    this.tipTarget.style.top = '';
  }

  position() {
    if (!this.anchor || !this.hasTipTarget) return;

    const tip = this.tipTarget;
    const anchorRect = this.anchor.getBoundingClientRect();
    const gap = 8;
    const pad = 8;

    if (!this.tipHeight) {
      tip.style.left = '-9999px';
      tip.style.top = '0px';
      this.tipHeight = tip.getBoundingClientRect().height;
    }

    const tipWidth = tip.getBoundingClientRect().width || tip.offsetWidth;
    let top = anchorRect.top - this.tipHeight - gap;
    let left = anchorRect.left + anchorRect.width / 2 - tipWidth / 2;

    left = Math.max(pad, Math.min(left, window.innerWidth - tipWidth - pad));

    if (top < pad) {
      top = anchorRect.bottom + gap;
    }

    tip.style.left = `${left}px`;
    tip.style.top = `${top}px`;
  }
}
